import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
    dateRangeToFilterInput,
    getURLSearchParams,
    updateAndReplaceURLSearchParams,
    filterInputToDateRange,
    ISODateRangeToTimestampFilter,
} from '@trqk/utils';
import { useStateWithCallback } from '@trqk/hooks';

import { DateRangeSelectorDropdown } from '../..';
import { defaultFilterHeight, defaultFilterWidth } from '../constants';

interface URLParamDateRangeQueryFilterProps<
    FilterInput extends Record<
        string,
        Maybe<TableStringFilterInput | TableIntFilterInput>
    >,
> {
    /**
     * Default option for the date range filter
     */
    defaultOption: DropdownOption<MaybeEmptyDateRange>;
    /**
     * The field on the data to filter by
     */
    fieldName: keyof FilterInput;
    /**
     * The filter input for the AppSync query
     */
    filterInput: FilterInput;
    /**
     * Height for the DateRangeSelectorDropdown
     */
    height?: number;
    /**
     * Method to set the filter input for the AppSync query
     */
    setFilterInput: SetState<FilterInput>;
    /**
     * Width for the DateRangeSelectorDropdown
     */
    width?: number;
    /**
     * The object returned by the useModal hook
     */
    modal: ModalProviderValue;
    urlParams: {
        /**
         * Name of the URL query param for the end date
         */
        end: string;
        /**
         * Name of the URL query param for the date range label
         */
        label: string;
        /**
         * Name of the URL query param for the start date
         */
        start: string;
    };
}

/**
 * Returns a React component which renders a DateRangeSelectorDropdown that allows the user to
 * set a date range for a TableStringFilterInput.
 *
 * @remarks
 * This filter is implemented as an isolated component (outside of the react-table filtering API)
 * because it is used to set the AppSync query filter input, rather than filtering the data that is
 * already passed to a react-table instance.
 * @param props - {@link URLParamDateRangeQueryFilterProps}
 */

export function URLParamDateRangeQueryFilter<
    FilterInput extends Record<
        string,
        Maybe<TableStringFilterInput | TableIntFilterInput>
    >,
>({
    defaultOption,
    fieldName,
    filterInput,
    height = defaultFilterHeight,
    modal,
    width = defaultFilterWidth,
    setFilterInput,
    urlParams,
}: URLParamDateRangeQueryFilterProps<FilterInput>): JSX.Element {
    const history = useHistory();

    useEffect(() => {
        const dateURLParams = getURLSearchParams({
            search: history.location.search,
            configs: [
                {
                    defaultValue: null,
                    paramKey: urlParams.end,
                },
                {
                    defaultValue: null,
                    paramKey: urlParams.start,
                },
            ],
        });

        const initialFilterInput = ISODateRangeToTimestampFilter({
            end: dateURLParams[urlParams.end],
            start: dateURLParams[urlParams.start],
        });

        if (initialFilterInput !== null) {
            // eslint-disable-next-line
            // @ts-ignore
            setFilterInput({ timestamp: initialFilterInput });
        }
    }, []);

    const onSetSelectedLabel = useCallback((nextLabel: string) => {
        updateAndReplaceURLSearchParams(history, [
            {
                key: urlParams.label,
                value: nextLabel,
            },
        ]);
    }, []);

    const [selectedLabel, setSelectedLabel] = useStateWithCallback(
        getURLSearchParams({
            search: history.location.search,
            configs: [
                {
                    defaultValue: defaultOption.label,
                    paramKey: urlParams.label,
                },
            ],
        })[urlParams.label] ?? defaultOption.label,
        onSetSelectedLabel,
    );

    return (
        <DateRangeSelectorDropdown
            // This filter happens before the data fetching phase, so it can't be reset to an empty
            // value (for example, by using the Clear button in PreFilters). We give it a distinct
            // border to illustrate that it is "always on".
            borderClasses="border border-secondary-500 rounded"
            dateRange={filterInputToDateRange(filterInput?.[fieldName] ?? null)}
            initialOption={defaultOption}
            selectedLabel={selectedLabel}
            setSelectedLabel={setSelectedLabel}
            setDateRange={(nextValue: MaybeEmptyDateRange): void => {
                setFilterInput((prev: FilterInput): FilterInput => {
                    const nextFilterInput = dateRangeToFilterInput(nextValue);

                    return {
                        ...prev,
                        [fieldName]: nextFilterInput,
                    };
                });
            }}
            height={height}
            modal={modal}
            width={width}
        />
    );
}
